import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { th as palette } from "@/lib/bricks";
import { Page, PageHeader, Stats, Section, List, Row, Empty, TextLink, Loading, humanize } from "@/components/calm/Calm";
import { shortDate } from "@/components/desk/deskLayout";
import { propertyCopy, isPropertyKind } from "@/lib/clientTypes";
import PropertyHubPage from "./PropertyHubPage";
import { BookMediaLink } from "@/components/listing/BookMedia";
import { useShellDark } from "@/lib/useShellDark";
import { summarize, KIND_LABEL } from "./builderDesk";

// Builder desk: every project's Site progress at a glance, from the same
// /dev/projects + /timeline data the Projects page uses. Nothing is estimated.
const MAX_TIMELINES = 8;
const WIDE = 1200; // two-column cards only when the side column fits a project name
const VISIT = { pending: ["Requested"], scheduled: ["Scheduled"], completed: ["Delivered"], failed: ["Not done"] };

function torontoToday() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
}
function greeting() {
  const h = Number(new Date().toLocaleString("en-CA", { hour: "numeric", hour12: false, timeZone: "America/Toronto" }));
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

// Builders get the site-progress desk; every other property account gets the
// hub shaped like its business (PropertyHubPage).
export default function DeveloperOverviewPage() {
  const { user } = useAuth();
  const k = user?.account_type;
  return k && k !== "builder" && isPropertyKind(k) ? <PropertyHubPage /> : <BuilderDesk />;
}

function BuilderDesk() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const dark = useShellDark();
  const p = palette(dark);
  const [projects, setProjects] = useState(null);
  const [timelines, setTimelines] = useState({});
  const [error, setError] = useState("");
  const [wide, setWide] = useState(() => typeof window === "undefined" || window.innerWidth >= WIDE);

  useEffect(() => {
    const on = () => setWide(window.innerWidth >= WIDE);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  useEffect(() => {
    let live = true;
    api.get("/dev/projects")
      .then(async (r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        if (!live) return;
        setProjects(list);
        const pairs = await Promise.all(list.slice(0, MAX_TIMELINES).map((pr) =>
          api.get(`/dev/projects/${pr.id}/timeline`).then((t) => [pr.id, t.data]).catch(() => [pr.id, null])));
        if (live) setTimelines(Object.fromEntries(pairs.filter(([, t]) => t && Array.isArray(t.visits))));
      })
      .catch((e) => { if (live) { setProjects([]); setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn’t load your projects."); } });
    return () => { live = false; };
  }, []);

  const copy = propertyCopy(user?.account_type);
  const firstName = (user?.name || "").split(" ")[0] || "there";
  const s = summarize(projects || [], timelines, torontoToday());
  const k = s.kpis;
  const stats = [
    { label: copy.things, value: k.projects },
    { label: "Visits delivered", value: k.delivered },
    { label: "Next visit", value: k.next ? shortDate(k.next.date) : "None" },
  ];

  return (
    <Page testid="builder-desk" width={1200}>
      <PageHeader title={`${greeting()}, ${firstName}.`}
        subtitle={copy.subtitle}
        action={<div style={{ display: "flex", gap: 18, alignItems: "center" }}>
          <Link to="/developer/projects" style={{ fontSize: 13, color: p.ink2, textUnderlineOffset: 3, textDecorationColor: p.hairS }}>{copy.things}</Link>
          <BookMediaLink href={copy.book.href} dark={dark} label={copy.book.label} testId="desk-book" />
        </div>} />

      {error && <div role="alert" style={{ fontSize: 13, color: "var(--c-attn)" }}>{error}</div>}

      {projects === null ? <Loading /> : (
        <>
          <Stats testid="builder-stats" items={stats} />

          <div style={{ display: "grid", gridTemplateColumns: wide ? "minmax(0, 2fr) minmax(0, 1fr)" : "minmax(0, 1fr)", gap: 40 }}>
            <Section title={copy.things} action={<TextLink onClick={() => navigate("/developer/projects")}>Open</TextLink>}>
              {!s.rows.length ? (
                <Empty>{copy.empty}</Empty>
              ) : (
                <List>
                  {s.rows.map((r) => (
                    <Row key={r.id} wide={false} onClick={() => navigate("/developer/projects")}
                      title={r.name}
                      meta={[r.place, r.loaded ? `${r.visits} visit${r.visits === 1 ? "" : "s"}` : null].filter(Boolean)}
                      next={r.last || r.kinds.length ? [r.last ? `Last ${shortDate(r.last)}` : null,
                        r.kinds.slice(0, 3).map((x) => KIND_LABEL[x] || x).join(", ") || null].filter(Boolean) : null}
                      status={r.monitoring ? "Monitored" : humanize(r.status || "Draft")} />
                  ))}
                </List>
              )}
            </Section>

            <Section title="Upcoming visits">
              {!s.upcoming.length ? <Empty>Nothing booked.</Empty> : (
                <List>
                  {s.upcoming.map((v) => (
                    <Row key={v.id} wide={false} onClick={() => navigate("/developer/projects")}
                      title={v.project}
                      meta={[{ text: v.date ? shortDate(v.date) : "Date to be set", figure: !!v.date }, v.phase_name].filter(Boolean)}
                      status={(VISIT[v.status] || [humanize(v.status)])[0]} attention={v.status === "failed"} />
                  ))}
                </List>
              )}
            </Section>
          </div>

          <Section title="Latest deliveries">
            {!s.latest.length ? <Empty>{copy.deliveries}</Empty> : (
              <List>
                {s.latest.map((v) => (
                  <Row key={v.id} wide={false} onClick={() => navigate("/developer/projects")}
                    title={v.project}
                    meta={[{ text: shortDate(v.date), figure: true }, (v.kinds || []).map((x) => KIND_LABEL[x] || x).join(", ")].filter(Boolean)}
                    status="Delivered" />
                ))}
              </List>
            )}
          </Section>
        </>
      )}
    </Page>
  );
}
